const SEO_SITE_ID = "6a0fea815c0f9b2e77467311";
const SEO_API_KEY = "digikit2026";
const SEO_PLATFORM_URL = "https://seo-platform-digikit.vercel.app";

/**
 * Fetch SEO data for a page from the SEO Platform.
 * Returns null if not found or API is unreachable.
 *
 * @param {string} slug
 * @param {{ siteId?: string, domain?: string, apiKey?: string }} [opts]
 * @returns {Promise<import('../types/seo').PageSeoData | null>}
 */
export async function fetchSeo(slug, opts = {}) {
  const siteId = opts.siteId || SEO_SITE_ID;
  const apiKey = opts.apiKey || SEO_API_KEY;

  if (!siteId && !opts.domain) {
    console.warn("[SEO] SEO platform site ID is not set");
    return null;
  }

  const normalizedSlug = slug.startsWith("/") ? slug : `/${slug}`;

  const params = new URLSearchParams({ slug: normalizedSlug });
  if (siteId) params.set("siteId", siteId);
  if (opts.domain) params.set("domain", opts.domain);

  try {
    const url = `${SEO_PLATFORM_URL}/api/public/seo/page?${params.toString()}`;
    const headers = {};

    if (apiKey) {
      headers["X-API-Key"] = apiKey;
    }

    const res = await fetch(url, { headers });

    if (res.status === 404) return null;
    if (!res.ok) {
      console.warn(`[SEO] API returned ${res.status} for slug "${slug}"`);
      return null;
    }

    return await res.json();
  } catch (err) {
    console.error("[SEO] Fetch failed:", err);
    return null;
  }
}

/**
 * Convert PageSeoData into a React Helmet-ready object.
 *
 * @param {import('../types/seo').PageSeoData | null} seo
 */
export function buildHelmetData(seo) {
  if (!seo) return null;

  const { meta, openGraph, twitter } = seo;

  return {
    title: meta.title,
    description: meta.description,
    keywords: Array.isArray(meta.keywords) ? meta.keywords.join(", ") : "",
    robots: meta.robots || "",
    canonical: meta.canonical || "",
    openGraph: {
      title: openGraph.title || meta.title,
      description: openGraph.description || meta.description,
      type: openGraph.type || "website",
      url: openGraph.url || meta.canonical || "",
      siteName: openGraph.siteName || "",
      image: openGraph.image || "",
      imageAlt: openGraph.imageAlt || openGraph.title || "",
    },
    twitter: {
      card: twitter.card || "summary_large_image",
      title: twitter.title || meta.title,
      description: twitter.description || meta.description,
      site: twitter.site || "",
      creator: twitter.creator || "",
      image: twitter.image || "",
      imageAlt: twitter.imageAlt || twitter.title || "",
    },
  };
}

/**
 * One-call convenience: fetch + convert to helmet data.
 *
 * @param {string} slug
 * @param {{ siteId?: string, domain?: string, apiKey?: string }} [opts]
 */
export async function getPageHelmetData(slug, opts) {
  const seo = await fetchSeo(slug, opts);
  return buildHelmetData(seo);
}
